import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Box, Toolbar, useMediaQuery, CircularProgress } from "@mui/material";
import { dt } from "../../../../shared/themes/redesignTokens";
import { useRedesignSetup } from "../../../../shared/hooks/useRedesignSetup";
import useTranslation from "../../../../shared/hooks/useTranslation";
import { useAppDispatch, useAppSelector } from "../../../../shared/hooks/sharedHooks";
import useOfferPresence from "../../../../shared/notifications/hooks/useOfferPresence";
import useDataRefresh from "../../../../shared/notifications/hooks/useDataRefresh";
import useVisibilityRefresh from "../../../../shared/hooks/useVisibilityRefresh";
import PageBreadcrumbs from "../../../../shared/components/PageBreadcrumbs";
import publicServiceRequestsApi from "../../../serviceBoard/api/publicServiceRequestsApi";
import publicCustomersApi from "../../../../public/api/publicCustomersApi";
import serviceRequestsApi from "../../api/serviceRequestsApi";
import { PublicServiceRequest } from "../../../serviceBoard/models/models";
import { Customer } from "../../../../public/models/customersModels";
import { ServiceRequestsState } from "../../models/models";
import { getServiceRequest, clearServiceRequest } from "../../customerServicingSlice";
import { getMachineParts } from "../../../machinery/customerMachineServiceReportsSlice";
import { ServiceReportsState } from "../../../machinery/models/ServiceReportsModel";
import { hideAccessDenied } from "../../../../public/accessDeniedSlice";
import RfsMiniMap from "./RfsMiniMap";
import { RfsMapData } from "./RfsMapModal";
import RfsCompanyCard from "./RfsCompanyCard";
import RfsHeaderStrip from "./RfsHeaderStrip";
import RfsIssueCard from "./RfsIssueCard";
import RfsMachineCard from "./RfsMachineCard";
import RfsCommercialTermsCard from "./RfsCommercialTermsCard";
import RfsAttachmentsCard from "./RfsAttachmentsCard";
import RfsActionBar from "./RfsActionBar";
import RfsUnifiedLogbook from "./RfsUnifiedLogbook";
import RfsStatusGuidanceBanner from "./RfsStatusGuidanceBanner";
import ServiceLifecycleTracker from "../../../shared/components/ServiceLifecycleTracker";
import { getOwnerLifecycleStages } from "../../../shared/utils/lifecycleStages";
import { ServiceRequestStatusCode } from "../../models/models";

// ===================================================================
//  KEYFRAMES (injected once via style tag)
// ===================================================================
const DETAIL_KEYFRAMES_ID = "rfs-detail-keyframes";
const DETAIL_KEYFRAMES = `
  @keyframes heroReveal {
    0%   { opacity: 0; transform: translateY(-12px); }
    100% { opacity: 1; transform: translateY(0); }
  }
  @keyframes sectionReveal {
    0%   { opacity: 0; transform: translateY(16px) scale(0.98); }
    100% { opacity: 1; transform: translateY(0) scale(1); }
  }
`;

const RfsDetailPage = () => {
  useRedesignSetup();
  const { translate } = useTranslation(["serviceBoard", "servicing"]);
  const navigate = useNavigate();
  const { id } = useParams();
  const dispatch = useAppDispatch();
  const presenceCounts = useOfferPresence();

  const isPhone = useMediaQuery("(max-width:768px)");
  const isTablet = useMediaQuery("(min-width:769px) and (max-width:1200px)");
  const isDesktop = useMediaQuery("(min-width:1201px)");

  // State
  const [sr, setSr] = useState<PublicServiceRequest | null>(null);
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  // Owner detection — derived from sr.isOwnCompany after the public SR loads
  const [isOwner, setIsOwner] = useState(false);

  // hasReviewed — tracks whether the owner has reviewed this completed SR
  // RfsUnifiedLogbook manages review state internally; this is for the guidance banner
  const [hasReviewed, setHasReviewed] = useState(false);

  // Ref for scroll-to functionality (guidance banner -> logbook)
  const logbookSectionRef = useRef<HTMLDivElement>(null);

  // Redux state for the authenticated customer SR (used for owner-specific data)
  const { serviceRequest: customerServiceRequest } =
    useAppSelector<ServiceRequestsState>((state: any) => state.customerServicing);

  // Machine parts for resolving affectedPartCategoryId -> name
  const { machineParts } = useAppSelector<ServiceReportsState>(
    (state: any) => state.customerServiceReports
  );

  // Inject page-specific keyframes
  useEffect(() => {
    if (!document.getElementById(DETAIL_KEYFRAMES_ID)) {
      const style = document.createElement("style");
      style.id = DETAIL_KEYFRAMES_ID;
      style.textContent = DETAIL_KEYFRAMES;
      document.head.appendChild(style);
    }
  }, []);

  // Fetch machine parts (for resolving affectedPartCategoryId to a display name)
  useEffect(() => {
    if (!machineParts || machineParts.length === 0) {
      dispatch(getMachineParts());
    }
  }, [dispatch, machineParts]);

  // Resolve affectedPartCategoryId -> display name using machineParts
  const resolvedPartName = useMemo(() => {
    if (!sr || !sr.affectedPartCategoryId || !machineParts || machineParts.length === 0) return undefined;
    for (const group of machineParts) {
      for (const part of group.parts) {
        if (part.id === sr.affectedPartCategoryId) {
          return part.name;
        }
      }
    }
    return undefined;
  }, [sr, machineParts]);

  // Fetch data — authenticated first, public fallback
  // The public endpoint only returns OpenToOffers SRs, so once an offer is accepted
  // (status changes), the public endpoint 404s. By trying the authenticated endpoint
  // first, owners always get their SR regardless of status.
  const fetchData = async () => {
    if (!id) return;
    try {
      setLoading(true);
      setError(false);

      // Step 1: Try authenticated endpoint first
      let ownerDetected = false;
      try {
        const authData = await serviceRequestsApi.getServiceRequest(id);
        // Success — user is the owner. Map to PublicServiceRequest shape for components.
        ownerDetected = true;
        setIsOwner(true);
        setSr({
          id: authData.id,
          internalNumber: authData.internalNumber,
          issue: authData.issue,
          description: authData.description,
          affectedPartCategoryId: authData.affectedPartCategoryId,
          openDate: authData.openDate ? String(authData.openDate) : undefined,
          statusCode: authData.statusCode,
          serviceTypeCode: authData.serviceTypeCode,
          priorityTypeCode: authData.priorityTypeCode,
          flexibleDates: authData.flexibleDates,
          serviceStartDate: authData.serviceStartDate ? String(authData.serviceStartDate) : undefined,
          serviceEndDate: authData.serviceEndDate ? String(authData.serviceEndDate) : undefined,
          worksiteAccessEnabled: authData.worksiteAccessEnabled,
          workingHoursFrom: authData.workingHoursFrom,
          workingHoursTo: authData.workingHoursTo,
          weekendWorkPossible: authData.weekendWorkPossible,
          afterHourAccessAvailable: authData.afterHourAccessAvailable,
          budgetEnabled: authData.budgetEnabled,
          budgetTypeCode: authData.budgetTypeCode,
          fixedBudget: authData.fixedBudget,
          budgetRangeFrom: authData.budgetRangeFrom,
          budgetRangeTo: authData.budgetRangeTo,
          paymentTermsEnabled: authData.paymentTermsEnabled,
          paymentTermsCode: authData.paymentTermsCode,
          notes: authData.notes,
          alarmCode: authData.alarmCode,
          machine: authData.machine as any,
          customer: { id: "", name: "" },
          serviceReportNumber: authData.serviceReportNumber,
          files: authData.files as any,
          offersCount: authData.offersCount ?? 0,
          isOwnCompany: true,
          visibilityCode: authData.visibilityCode,
          targetPartnerId: authData.targetPartnerId,
          targetPartnerName: authData.targetPartnerName,
          createdDate: String(authData.createdDate),
          modifiedDate: String(authData.modifiedDate),
        } as PublicServiceRequest);
        // Also populate Redux for rich owner data (offersCount, acceptedOffer, etc.)
        dispatch(getServiceRequest(id));
      } catch (authErr: any) {
        // Step 2: Auth failed (403 = not owner, or not logged in) — fall back to public
        const publicData = await publicServiceRequestsApi.getServiceRequestAsync(id);
        setSr(publicData);
        dispatch(hideAccessDenied());

        const ownCompany = publicData.isOwnCompany === true;
        ownerDetected = ownCompany;
        setIsOwner(ownCompany);

        // If public endpoint reveals owner (OpenToOffers status), also fetch rich Redux data
        if (ownCompany) {
          dispatch(getServiceRequest(id));
        }

        // Fetch customer for contact info and map coords (provider needs this for map + company card)
        if (!ownCompany && publicData.customer?.id) {
          try {
            const custData = await publicCustomersApi.getCustomer(publicData.customer.id);
            setCustomer(custData);
          } catch (err) {
            console.error("Failed to load customer:", err);
          }
        }
      }
    } catch (err) {
      console.error("Failed to load service request:", err);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [id]);

  // Cleanup: clear customer SR from Redux on unmount to prevent stale data
  useEffect(() => {
    return () => {
      dispatch(clearServiceRequest());
    };
  }, [dispatch]);

  // Real-time refresh — fetchData() handles both public and authenticated paths internally
  // Listen to all relevant scopes: broadcast (serviceBoard), owner list (myServiceRequests),
  // and individual SR changes (serviceRequest) for actions like confirm/revision/review
  useDataRefresh("serviceBoard", () => {
    if (id) fetchData();
  });

  useDataRefresh("myServiceRequests", () => {
    if (id) fetchData();
  });

  useDataRefresh("serviceRequest", () => {
    if (id) fetchData();
  });

  useVisibilityRefresh(() => {
    if (id) fetchData();
  });

  // Handlers
  const handleCreateOffer = () => {
    if (id) navigate(`/service-offers/new/${id}`);
  };

  const handleBack = () => {
    if (isOwner) {
      navigate("/service-requests");
    } else {
      navigate("/service-board");
    }
  };

  // Callback for when an offer action occurs (accept/reject) inside the offers section
  // fetchData() handles both authenticated and public paths + Redux dispatch internally
  const handleOfferAction = () => {
    if (id) {
      fetchData();
    }
  };

  // Build map data from SR + customer for the mini map / modal
  // Must be before early returns to satisfy rules of hooks
  // Only needed for provider view (owners don't see the map)
  const rfsMapData: RfsMapData | null = useMemo(() => {
    if (!sr || !customer || isOwner) return null;
    const latStr = customer.address?.latitude;
    const lngStr = customer.address?.longitude;
    if (!latStr || !lngStr) return null;
    const lat = parseFloat(latStr);
    const lng = parseFloat(lngStr);
    if (isNaN(lat) || isNaN(lng)) return null;
    return {
      ownerLat: lat,
      ownerLng: lng,
      ownerName: sr.customer?.name || "",
      ownerLogoUrl: sr.customer?.logoUrl,
      ownerCity: customer.address?.city,
      priorityTypeCode: sr.priorityTypeCode,
      internalNumber: sr.internalNumber,
      ownerCustomerId: sr.customer?.id,
      serviceRequestId: sr.id,
    };
  }, [customer, sr, isOwner]);

  // Loading state
  if (loading && !sr) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh" }}>
        <CircularProgress sx={{ color: dt.blue }} />
      </Box>
    );
  }

  if (error || !sr) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh" }}>
        <Box sx={{ fontSize: "14px", fontFamily: dt.font, color: dt.textSecondary }}>
          {translate("serviceBoard:noServiceRequestsFound")}
        </Box>
      </Box>
    );
  }

  const preparingCount = presenceCounts[sr.id] || 0;
  const isOwnRequest = sr.isOwnCompany === true;

  // Breadcrumbs: owner sees "My Service Requests / RFS-XXX", provider sees "Service Board / RFS-XXX"
  const breadcrumbItems = isOwner
    ? [
        { title: translate("servicing:myServiceRequests"), link: "/service-requests" },
        { title: sr.internalNumber },
      ]
    : [
        { title: translate("serviceBoard:serviceBoard"), link: "/service-board" },
        { title: sr.internalNumber },
      ];

  // Determine status code for the lifecycle stepper (owner only)
  // Prefer customerServiceRequest (authenticated, richer data) over sr (public, may be less granular)
  const ownerStatusCode: ServiceRequestStatusCode | null = isOwner
    ? (customerServiceRequest?.statusCode ?? sr.statusCode ?? null)
    : null;

  // Show stepper only for owners when not in Draft status
  const showLifecycleStepper =
    isOwner &&
    ownerStatusCode !== null &&
    ownerStatusCode !== ServiceRequestStatusCode.draft;

  // Lifecycle stepper — rendered in hero position above content columns (owner only)
  // NOTE: ServiceLifecycleTracker "full" variant already renders its own card container
  // (cardBg, border, shadow, padding, borderRadius), so we only add animation + spacing here.
  const renderLifecycleStepper = () => {
    if (!showLifecycleStepper || ownerStatusCode === null) return null;
    return (
      <Box
        sx={{
          marginBottom: "20px",
          animation: "sectionReveal 0.5s ease-out both",
        }}
      >
        <ServiceLifecycleTracker
          stages={getOwnerLifecycleStages(ownerStatusCode)}
          variant="full"
        />
      </Box>
    );
  };

  // Scroll handlers for the guidance banner
  const handleScrollToOffers = () => {
    logbookSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleScrollToProgress = () => {
    logbookSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // Status guidance banner — rendered below stepper, above content (owner only)
  const renderGuidanceBanner = () => {
    if (!isOwner || ownerStatusCode === null || ownerStatusCode === ServiceRequestStatusCode.draft) {
      return null;
    }
    return (
      <Box sx={{ marginBottom: "16px" }}>
        <RfsStatusGuidanceBanner
          statusCode={ownerStatusCode}
          offersCount={customerServiceRequest?.offersCount || 0}
          newOffersCount={customerServiceRequest?.newOffersCount || 0}
          acceptedOffer={customerServiceRequest?.acceptedOffer}
          hasReviewed={hasReviewed}
          onScrollToOffers={handleScrollToOffers}
          onScrollToProgress={handleScrollToProgress}
        />
      </Box>
    );
  };

  // Unified logbook — replaces RfsOffersSection + RfsProgressSection for owners
  const renderLogbookSection = () => {
    if (!isOwner || ownerStatusCode === null || ownerStatusCode === ServiceRequestStatusCode.draft) return null;
    return (
      <Box ref={logbookSectionRef} sx={{ animation: "sectionReveal 0.4s ease-out 0.2s both" }}>
        <RfsUnifiedLogbook
          serviceRequestId={sr.id}
          statusCode={ownerStatusCode}
          createdDate={sr.createdDate}
          offersCount={customerServiceRequest?.offersCount || 0}
          newOffersCount={customerServiceRequest?.newOffersCount || 0}
          acceptedOffer={customerServiceRequest?.acceptedOffer}
          serviceRequestIssue={sr.issue || ""}
          onOfferAction={handleOfferAction}
          onHasReviewedChange={setHasReviewed}
        />
      </Box>
    );
  };

  // ===================================================================
  // Data refresh handler for owner actions (archive/unarchive)
  const handleOwnerDataRefresh = () => {
    if (id) {
      fetchData();
    }
  };

  //  OWNER DESKTOP LAYOUT: 2-column (70% content / 30% machine)
  // ===================================================================
  const renderOwnerDesktopLayout = () => (
    <Box sx={{ maxWidth: 1280, margin: "0 auto", px: 3, py: 2.5 }}>
      {renderLifecycleStepper()}
      {renderGuidanceBanner()}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "70% 30%",
          gap: 2.5,
        }}
      >
      {/* MAIN COLUMN — Header, Issue, Terms, Attachments, Offers, Progress, Action Bar */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2.5,
          animation: "heroReveal 0.4s ease-out both",
        }}
      >
        <RfsHeaderStrip
          sr={sr}
          preparingCount={preparingCount}
          isOwner={true}
          statusCode={ownerStatusCode !== null ? ownerStatusCode : undefined}
          serviceRequestId={sr.id}
          onDataRefresh={handleOwnerDataRefresh}
        />
        <RfsIssueCard sr={sr} affectedPartName={resolvedPartName} />
        <RfsCommercialTermsCard sr={sr} />
        <RfsAttachmentsCard files={sr.files || []} />
        {renderLogbookSection()}
        <RfsActionBar
          onCreateOffer={handleCreateOffer}
          onBack={handleBack}
          isOwnRequest={isOwnRequest}
          hasMyOffer={sr.hasMyOffer}
          isOwner={isOwner}
        />
      </Box>

      {/* SIDE COLUMN — Machine only */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          animation: "sectionReveal 0.4s ease-out 0.1s both",
        }}
      >
        <RfsMachineCard machine={sr.machine} />
      </Box>
      </Box>
    </Box>
  );

  // ===================================================================
  //  OWNER TABLET LAYOUT: single column, no map, no company card
  // ===================================================================
  const renderOwnerTabletLayout = () => (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        maxWidth: 900,
        margin: "0 auto",
        px: 2,
        py: 2,
      }}
    >
      {renderLifecycleStepper()}
      {renderGuidanceBanner()}
      {/* Header + Issue — full width at top */}
      <Box sx={{ animation: "heroReveal 0.4s ease-out both" }}>
        <RfsHeaderStrip
          sr={sr}
          preparingCount={preparingCount}
          isOwner={true}
          statusCode={ownerStatusCode !== null ? ownerStatusCode : undefined}
          serviceRequestId={sr.id}
          onDataRefresh={handleOwnerDataRefresh}
        />
      </Box>
      <Box sx={{ animation: "heroReveal 0.4s ease-out 0.05s both" }}>
        <RfsIssueCard sr={sr} affectedPartName={resolvedPartName} />
      </Box>

      {/* Machine card — full width */}
      <Box sx={{ animation: "sectionReveal 0.4s ease-out 0.1s both" }}>
        <RfsMachineCard machine={sr.machine} />
      </Box>

      {/* Terms + Attachments — full width */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          animation: "sectionReveal 0.4s ease-out 0.15s both",
        }}
      >
        <RfsCommercialTermsCard sr={sr} />
        <RfsAttachmentsCard files={sr.files || []} />
      </Box>

      {/* Unified Logbook */}
      {renderLogbookSection()}

      {/* Spacer for fixed bottom bar */}
      <Box sx={{ height: 80 }} />
      <RfsActionBar
        onCreateOffer={handleCreateOffer}
        onBack={handleBack}
        isOwnRequest={isOwnRequest}
        hasMyOffer={sr.hasMyOffer}
        isOwner={isOwner}
      />
    </Box>
  );

  // ===================================================================
  //  OWNER PHONE LAYOUT: single column, no map, no company card
  // ===================================================================
  const renderOwnerPhoneLayout = () => (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        px: 1.5,
        py: 1.5,
      }}
    >
      {renderLifecycleStepper()}
      {renderGuidanceBanner()}
      <Box sx={{ animation: "heroReveal 0.4s ease-out both" }}>
        <RfsHeaderStrip
          sr={sr}
          preparingCount={preparingCount}
          isOwner={true}
          statusCode={ownerStatusCode !== null ? ownerStatusCode : undefined}
          serviceRequestId={sr.id}
          onDataRefresh={handleOwnerDataRefresh}
        />
      </Box>
      <Box sx={{ animation: "heroReveal 0.4s ease-out 0.05s both" }}>
        <RfsIssueCard sr={sr} affectedPartName={resolvedPartName} />
      </Box>
      <Box sx={{ animation: "sectionReveal 0.4s ease-out 0.1s both" }}>
        <RfsMachineCard machine={sr.machine} />
      </Box>
      <Box sx={{ animation: "sectionReveal 0.4s ease-out 0.12s both" }}>
        <RfsCommercialTermsCard sr={sr} />
      </Box>
      <Box sx={{ animation: "sectionReveal 0.4s ease-out 0.14s both" }}>
        <RfsAttachmentsCard files={sr.files || []} />
      </Box>
      {/* Unified Logbook */}
      {renderLogbookSection()}
      {/* Spacer for fixed bottom bar */}
      <Box sx={{ height: 72 }} />
      <RfsActionBar
        onCreateOffer={handleCreateOffer}
        onBack={handleBack}
        isOwnRequest={isOwnRequest}
        hasMyOffer={sr.hasMyOffer}
        isOwner={isOwner}
      />
    </Box>
  );

  // ===================================================================
  //  PROVIDER DESKTOP LAYOUT: 3-column (25 / 50 / 25) — unchanged
  // ===================================================================
  const renderDesktopLayout = () => (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "25% 50% 25%",
        gap: 2.5,
        maxWidth: 1280,
        margin: "0 auto",
        px: 3,
        py: 2.5,
      }}
    >
      {/* LEFT COLUMN — Map + Company */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          animation: "sectionReveal 0.4s ease-out 0.1s both",
        }}
      >
        <RfsMiniMap mapData={rfsMapData} />
        <RfsCompanyCard
          embeddedCustomer={sr.customer}
          customer={customer}
          isDirectToMe={sr.isDirectToMe}
        />
      </Box>

      {/* CENTRE COLUMN — Problem (hero) + Attachments */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2.5,
          animation: "heroReveal 0.4s ease-out both",
        }}
      >
        <RfsHeaderStrip sr={sr} preparingCount={preparingCount} />
        <RfsIssueCard sr={sr} affectedPartName={resolvedPartName} />
        <RfsCommercialTermsCard sr={sr} />
        <RfsAttachmentsCard files={sr.files || []} />
        <RfsActionBar
          onCreateOffer={handleCreateOffer}
          onBack={handleBack}
          isOwnRequest={isOwnRequest}
          hasMyOffer={sr.hasMyOffer}
          isOwner={false}
        />
      </Box>

      {/* RIGHT COLUMN — Machine only */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          animation: "sectionReveal 0.4s ease-out 0.15s both",
        }}
      >
        <RfsMachineCard machine={sr.machine} />
      </Box>
    </Box>
  );

  // ===================================================================
  //  PROVIDER TABLET LAYOUT: Single col at top, then 50/50 pairs — unchanged
  // ===================================================================
  const renderTabletLayout = () => (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        maxWidth: 900,
        margin: "0 auto",
        px: 2,
        py: 2,
      }}
    >
      {/* Header + Issue — full width at top */}
      <Box sx={{ animation: "heroReveal 0.4s ease-out both" }}>
        <RfsHeaderStrip sr={sr} preparingCount={preparingCount} />
      </Box>
      <Box sx={{ animation: "heroReveal 0.4s ease-out 0.05s both" }}>
        <RfsIssueCard sr={sr} affectedPartName={resolvedPartName} />
      </Box>

      {/* Map+Company | Machine — side by side */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 2,
          animation: "sectionReveal 0.4s ease-out 0.1s both",
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <RfsMiniMap mapData={rfsMapData} />
          <RfsCompanyCard
            embeddedCustomer={sr.customer}
            customer={customer}
            isDirectToMe={sr.isDirectToMe}
          />
        </Box>
        <Box>
          <RfsMachineCard machine={sr.machine} />
        </Box>
      </Box>

      {/* Terms + Attachments — full width */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          animation: "sectionReveal 0.4s ease-out 0.15s both",
        }}
      >
        <RfsCommercialTermsCard sr={sr} />
        <RfsAttachmentsCard files={sr.files || []} />
      </Box>

      {/* CTA sticky handled by RfsActionBar */}
      {/* Spacer for fixed bottom bar */}
      <Box sx={{ height: 80 }} />
      <RfsActionBar
        onCreateOffer={handleCreateOffer}
        onBack={handleBack}
        isOwnRequest={isOwnRequest}
        hasMyOffer={sr.hasMyOffer}
        isOwner={false}
      />
    </Box>
  );

  // ===================================================================
  //  PROVIDER PHONE LAYOUT: single column, no map — unchanged
  // ===================================================================
  const renderPhoneLayout = () => (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        px: 1.5,
        py: 1.5,
      }}
    >
      <Box sx={{ animation: "heroReveal 0.4s ease-out both" }}>
        <RfsHeaderStrip sr={sr} preparingCount={preparingCount} />
      </Box>
      <Box sx={{ animation: "heroReveal 0.4s ease-out 0.05s both" }}>
        <RfsIssueCard sr={sr} affectedPartName={resolvedPartName} />
      </Box>
      <Box sx={{ animation: "sectionReveal 0.4s ease-out 0.1s both" }}>
        <RfsMachineCard machine={sr.machine} />
      </Box>
      <Box sx={{ animation: "sectionReveal 0.4s ease-out 0.12s both" }}>
        <RfsCompanyCard
          embeddedCustomer={sr.customer}
          customer={customer}
          isDirectToMe={sr.isDirectToMe}
        />
      </Box>
      <Box sx={{ animation: "sectionReveal 0.4s ease-out 0.14s both" }}>
        <RfsCommercialTermsCard sr={sr} />
      </Box>
      <Box sx={{ animation: "sectionReveal 0.4s ease-out 0.16s both" }}>
        <RfsAttachmentsCard files={sr.files || []} />
      </Box>
      {/* Spacer for fixed bottom bar */}
      <Box sx={{ height: 72 }} />
      <RfsActionBar
        onCreateOffer={handleCreateOffer}
        onBack={handleBack}
        isOwnRequest={isOwnRequest}
        hasMyOffer={sr.hasMyOffer}
        isOwner={false}
      />
    </Box>
  );

  return (
    <>
      {/* Breadcrumbs */}
      <Toolbar className="flex items-center justify-between w-full">
        <Box className="heading-2">
          <PageBreadcrumbs items={breadcrumbItems} />
        </Box>
      </Toolbar>

      {/* Page background */}
      <Box
        sx={{
          flex: 1,
          width: "100%",
          minHeight: "calc(100vh - 64px)",
          background: dt.pageBg,
          fontFamily: dt.font,
          "& *:not(.MuiSvgIcon-root)": { fontFamily: dt.font },
          backgroundImage: `radial-gradient(${dt.pageDotColor} 1px, transparent 1px)`,
          backgroundSize: "20px 20px",
        }}
      >
        {isOwner ? (
          <>
            {(isDesktop || (!isTablet && !isPhone)) && renderOwnerDesktopLayout()}
            {isTablet && renderOwnerTabletLayout()}
            {isPhone && renderOwnerPhoneLayout()}
          </>
        ) : (
          <>
            {(isDesktop || (!isTablet && !isPhone)) && renderDesktopLayout()}
            {isTablet && renderTabletLayout()}
            {isPhone && renderPhoneLayout()}
          </>
        )}
      </Box>
    </>
  );
};

export default RfsDetailPage;
